"use client";

import { useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { Skeleton } from "@/shadcn-components/ui/skeleton";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components/ui/popover";
import { Calendar } from "@/shadcn-components/ui/calendar";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import { useEconomicCalendarViewModel } from "./useEconomicCalendarViewModel";
import { calendarDayKeyLocal } from "./selectors";
import { cn } from "@/lib/utils";
import { EconomicCalendarScheduleTable } from "./EconomicCalendarScheduleTable";
import { EconomicCalendarCatalogList } from "./EconomicCalendarCatalogList";
import { EconomicSeriesDetailPanel } from "./EconomicSeriesDetailPanel";
import {
  filterEventsBySeriesKey,
  seriesKey,
} from "@/vybe-trader-client/economic-calendar/series-key";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shadcn-components/ui/resizable";

interface EconomicCalendarViewProps {
  variant: "settings" | "desktop";
  onBack?: () => void;
  onOpenEventDetail?: (event: EconomicEvent) => void;
}

function formatPopoverDate(date: Date) {
  return date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .replace(",", "");
}

type LeftPaneMode = "day" | "catalog";
type SelectionContext = "day" | "catalog" | "none";

export default function EconomicCalendarView({
  variant,
  onBack,
  onOpenEventDetail,
}: EconomicCalendarViewProps) {
  const vm = useEconomicCalendarViewModel();

  const [leftPaneMode, setLeftPaneMode] = useState<LeftPaneMode>("day");
  const [selectionContext, setSelectionContext] =
    useState<SelectionContext>("none");

  const [desktopSeriesKey, setDesktopSeriesKey] = useState<string | null>(
    null
  );
  const [detailPaneVisible, setDetailPaneVisible] = useState(true);

  const handleDateNav = (dir: -1 | 1) => {
    const nd = new Date(vm.selectedDate);
    nd.setDate(nd.getDate() + dir);
    vm.setSelectedDate(nd);
  };

  const awaiting = vm.awaitingData;
  const isError = vm.loadingStatus === "error";

  const pickLatestForCatalog = (key: string): EconomicEvent | undefined => {
    const occ = filterEventsBySeriesKey(vm.allEvents, key);
    return occ.length ? occ[occ.length - 1] : undefined;
  };

  const onScheduleActivate = (e: EconomicEvent) => {
    if (variant === "settings") {
      onOpenEventDetail?.(e);
      return;
    }
    setSelectionContext("day");
    setDesktopSeriesKey(seriesKey(e));
    setDetailPaneVisible(true);
  };

  const onCatalogActivate = (key: string) => {
    if (variant === "settings") {
      const ev = pickLatestForCatalog(key);
      if (ev) {
        onOpenEventDetail?.(ev);
      }
      return;
    }
    setSelectionContext("catalog");
    setDesktopSeriesKey(key);
    setDetailPaneVisible(true);
  };

  const detailSubtitle =
    variant === "desktop" && selectionContext === "day"
      ? `Calendar · ${formatPopoverDate(vm.selectedDate)}`
      : undefined;

  const leftColumn = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex h-9 shrink-0 flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-2 py-px">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap">
          View
        </span>
        <div className="flex items-center rounded-md border border-border bg-muted/40 p-px gap-px">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 gap-1 rounded-sm px-2 text-[11px]",
              leftPaneMode === "day"
                ? "bg-background shadow-sm text-foreground"
                : ""
            )}
            aria-pressed={leftPaneMode === "day"}
            onClick={() => setLeftPaneMode("day")}
            title="Schedule for calendar day"
          >
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            Day
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(
              "h-7 gap-1 rounded-sm px-2 text-[11px]",
              leftPaneMode === "catalog"
                ? "bg-background shadow-sm text-foreground"
                : ""
            )}
            aria-pressed={leftPaneMode === "catalog"}
            onClick={() => setLeftPaneMode("catalog")}
            title="Browse all loaded series"
          >
            <LayoutList className="h-3.5 w-3.5 shrink-0" />
            Catalog
          </Button>
        </div>

        <div className="flex flex-1" />

        {variant === "desktop" ? (
          <Button
            type="button"
            variant={detailPaneVisible ? "secondary" : "outline"}
            size="sm"
            className={cn(
              "h-7 shrink-0 gap-1 px-2 text-[11px]",
              !detailPaneVisible && "border-primary text-primary"
            )}
            aria-pressed={detailPaneVisible}
            onClick={() => setDetailPaneVisible((v) => !v)}
            title={
              detailPaneVisible
                ? "Hide series detail pane"
                : "Show series detail pane"
            }
          >
            {detailPaneVisible ? (
              <>
                <PanelRightClose className="h-3.5 w-3.5" />
                Detail
              </>
            ) : (
              <>
                <PanelRightOpen className="h-3.5 w-3.5" />
                Detail
              </>
            )}
          </Button>
        ) : null}
      </div>

      {leftPaneMode === "day" ? (
        <EconomicCalendarScheduleTable
          events={vm.displayEvents}
          onRowClick={onScheduleActivate}
        />
      ) : (
        <EconomicCalendarCatalogList
          allEvents={vm.allEvents}
          onSeriesRowClick={onCatalogActivate}
        />
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-background text-foreground min-h-0 text-xs">
      {variant === "settings" && onBack && (
        <div className="flex h-10 items-center border-b border-border px-2 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onBack}
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4 text-primary" />
          </Button>
          <span className="flex-1 text-center font-medium text-muted-foreground">
            Economic calendar
          </span>
          <span className="w-8 shrink-0" aria-hidden />
        </div>
      )}

      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => handleDateNav(-1)}
            aria-label="Previous day"
          >
            <ChevronLeft className="h-4 w-4 text-muted-foreground" />
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 min-w-0 flex-1 max-w-[200px] gap-1 px-2 text-xs font-normal"
              >
                <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {formatPopoverDate(vm.selectedDate)}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="center">
              <Calendar
                mode="single"
                selected={vm.selectedDate}
                onSelect={(d) => d && vm.setSelectedDate(d)}
                modifiers={{
                  vybeMarkedDay: (day) =>
                    (vm.eventCountsByDay.get(calendarDayKeyLocal(day)) ??
                      0) > 0,
                }}
                modifiersClassNames={{
                  vybeMarkedDay:
                    "[&_button]:underline [&_button]:decoration-primary/70 [&_button]:decoration-2 [&_button]:underline-offset-2",
                }}
              />
            </PopoverContent>
          </Popover>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => handleDateNav(1)}
            aria-label="Next day"
          >
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>

        {/* calendar nav only — day/catalog moved into body row */}
      </div>

      {isError && vm.errorMessage && (
        <div className="shrink-0 border-b border-destructive/30 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
          {vm.errorMessage}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {awaiting ? (
          <div className="space-y-1 p-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full rounded-sm" />
            ))}
          </div>
        ) : variant === "desktop" && detailPaneVisible ? (
          <ResizablePanelGroup
            direction="horizontal"
            className="min-h-0 flex-1"
          >
            <ResizablePanel
              defaultSize={55}
              minSize={35}
              className="flex min-h-0 min-w-0 flex-col"
            >
              {leftColumn}
            </ResizablePanel>
            <ResizableHandle withHandle className="w-3 shrink-0 bg-border px-px" />
            <ResizablePanel
              defaultSize={45}
              minSize={22}
              className="flex min-h-0 min-w-0 flex-col"
            >
              <EconomicSeriesDetailPanel
                seriesKey={desktopSeriesKey}
                allEvents={vm.allEvents}
                subtitle={detailSubtitle}
                onHidePanel={() => setDetailPaneVisible(false)}
                className="border-l-0"
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          leftColumn
        )}
      </div>
    </div>
  );
}
